import Vue from "vue";
import Vuex from "vuex";
import {
  ADD_ERRORS,
  DELETE_ERROR,
  SET_PROGRESS,
  SET_CONFIG,
  SET_ERRORS,
  SET_FIELD,
  SET_FORM_DATA,
  SET_LOCK,
  SET_SELECT_STATE,
  SET_STEP,
  SET_VALID_FIELD,
  SET_DARK_BACKGROUND,
  SET_GLOBAL_ERROR,
  SET_RESPONSE,
  LOAD_TMSC,
  LOAD_QUERY,
  SET_ROUTE
} from "./constants/mutation-types";
import { CallbackService } from "@/services/CallbackService";
import {
  AjaxActionInterface,
  AppConfig,
  BluemediaGateway,
  FieldInterface,
  Response,
  ResponseError,
  ValidFieldInterface
} from "@/types";
import AjaxService from "@/services/AjaxService";
import { ErrorHelper } from "@/helpers/ErrorHelper";
import { UtilityHelper } from "@/helpers/UtilityHelper";
import { Locale } from "@/constants/locale";
import { Action, ResponseStatus } from "@/constants/ajax";
import { userInfo } from "@/constants/user-information";
import { CountryHelper } from "@/helpers/CountryHelper";
import { ServerResponse } from "@/models/ServerResponse";
import { NavigationService } from "@/services/NavigationService";
import { DataLayerService } from "@/services/DataLayerService";
import { AnalyticsService } from "@/services/AnalyticsService";
import { CustomError } from "@/models/CustomError";
import i18n, { loadLocaleMessagesAsync } from "@/i18n";
import Field from "@/components/basic/fields/Field.vue";
import { AdjustService } from "@/services/AdjustService";
import { UrlHelper } from "@/helpers/UrlHelper";
import { CookieHelper } from "@/helpers/CookieHelper";
import router from "@/router";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    previousStep: null,
    currentStep: "newAccount" as string, // todo: check if used
    timeToFinish: 8 as number,
    config: {
      version: undefined,
      taxOffices: [],
      citizenship: [],
      countries: [],
      phonePrefixesList: [],
      europeanCountries: [],
      bluemediaGatewayList: [] as BluemediaGateway[],
      tinCountries: []
    } as AppConfig,
    ajaxService: (undefined as unknown) as AjaxService,
    navigationService: (undefined as unknown) as NavigationService,
    dataLayerService: (undefined as unknown) as DataLayerService,
    analyticsService: (undefined as unknown) as AnalyticsService,
    adjustService: AdjustService,
    // <editor-fold desc="Error handling">
    errors: new Map() as Map<string, ResponseError>,
    isErrorsChanged: false,
    validFields: new Set() as Set<string>,
    isValidFieldsChanged: false,
    globalError: null as null | string,
    // </editor-fold>
    isMobile: false,
    isSelectOpen: null,
    isInitComplete: false,
    isLocked: true,
    isDarkBackground: false,
    appVersion: process.env.NODE_ENV,
    isDetectedMobileDevice: UtilityHelper.isDetectedMobileDevice(),
    formData: {},
    userInformation: {},
    response: null,
    tmsc: {},
    query: {},
    consents: [],
    isPhotoInstructionsOpen: false,
    isLoaderText: false,
    route: null,
    isLoadingThumbnail: false,
    AdjustUrlParams: "",
    residenceCountry: "",
    showLongDisclaimer: true,
    isGbg: false,
    showInstructionPopup: false,
    showSecurityPopup: false,
    isUploadDocumentStep: false,
    showInstructions: false,
    clientIdData: {},
    isSubheaderVisible: false,
  },
  mutations: {
    [SET_SELECT_STATE](state: any, value: boolean) {
      state.isSelectOpen = value;
    },

    [SET_LOCK](state: any, value: boolean) {
      state.isLocked = value;
    },

    [SET_FIELD](state: any, field: FieldInterface) {
      // @ts-ignore
      state[field.key] = field.value;
    },

    [SET_CONFIG](state: any, field: FieldInterface) {
      // @ts-ignore
      state.config[field.key] = field.value;
    },

    [SET_STEP](state: any, step: string) {
      state.previousStep = state.currentStep;
      state.currentStep = step;
    },

    // <editor-fold desc="Form data">
    [SET_FORM_DATA](state, formData) {
      state.formData = formData;
    },
    // </editor-fold>

    // <editor-fold desc="Errors">
    [SET_ERRORS](state: any, errors: ResponseError[]) {
      const errorMap = new Map();

      errors.forEach(error => {
        errorMap.set(error.field, error.message);
      });

      state.errors = errorMap;
      state.isErrorsChanged = !state.isErrorsChanged;
    },

    [ADD_ERRORS](state: any, errors: ResponseError[]) {
      const errorMap = state.errors;

      errors.forEach(error => {
        errorMap.set(error.field, error.message);
      });

      state.errors = errorMap;
      state.isErrorsChanged = !state.isErrorsChanged;
    },

    [DELETE_ERROR](state: any, errorName: string) {
      state.errors.delete(errorName);
      state.isErrorsChanged = !state.isErrorsChanged;
    },

    [SET_VALID_FIELD](state: any, data: ValidFieldInterface) {
      if (data.isValid) {
        state.validFields.add(data.name);
      } else {
        state.validFields.delete(data.name);
      }

      state.isValidFieldsChanged = !state.isValidFieldsChanged;
    },
    // </editor-fold>

    [SET_DARK_BACKGROUND](state: any, value: boolean) {
      state.isDarkBackground = value;
    },

    [SET_PROGRESS](state: any, value: number) {
      state.timeToFinish = value;
    },

    [SET_GLOBAL_ERROR](state: any, value: number) {
      state.globalError = value;
    },
    [SET_RESPONSE](state: any, value: Response) {
      state.response = value;
    },
    [LOAD_TMSC](state: any, tmscData: any) {
      const tmsc = { ...state.tmsc, ...tmscData };

      for (const key in tmsc) {
        if (undefined === tmsc[key]) {
          delete tmsc[key];
        }
      }

      state.tmsc = tmsc;

      const cookieDomain = UrlHelper.getCookiesDomainUrl();
      CookieHelper.save("tmsc", encodeURIComponent(JSON.stringify(state.tmsc)), 3600 * 24 * 30, cookieDomain);
    },
    [LOAD_QUERY](state: any, value: Response) {
      state.query = router.currentRoute.query;
    },

    [SET_ROUTE](state: any, route: any) {
      state.route = route;
    }

  },
  actions: {
    async init({ commit, state }) {
      if (!state.isInitComplete) {
        try {
          commit(SET_STEP, router.currentRoute.name);
          state.adjustService = new AdjustService();
          state.config.version = i18n.locale === Locale.PL ? "pl" : "eu";
          state.isMobile = UtilityHelper.isRouteMobile();
          state.dataLayerService = new DataLayerService(this);
          state.analyticsService = new AnalyticsService();
          state.ajaxService = new AjaxService(state);
          state.navigationService = new NavigationService(state.dataLayerService,state.analyticsService, state.adjustService, state.ajaxService, commit, this);


          commit(LOAD_TMSC);

          commit(LOAD_QUERY);
          const dictionariesRequest = state.ajaxService.runAction(Action.DICTIONARIES, {});
          const translationRequest = loadLocaleMessagesAsync(i18n.locale);

          Promise.all([dictionariesRequest, translationRequest]).then(values => {
            const response = values[0];

            if (ResponseStatus.ERROR !== response.status) {
              commit(SET_CONFIG, {
                key: "taxOffices",
                value: Object.entries(response.taxOfficeList)
              });

              const citizenshipList = Object.entries(response.citizenship).map(entry => {
                return {
                  key: entry[0],
                  value: entry[1]
                };
              });

              commit(SET_CONFIG, {
                key: "citizenship",
                value: CountryHelper.orderCountries(citizenshipList)
              });

              const countries: FieldInterface[] = Object.entries(response.countries).map(entry => {
                return {
                  key: entry[0],
                  value: entry[1]
                };
              });

              commit(SET_CONFIG, {
                key: "countries",
                value: CountryHelper.orderCountries(countries)
              });

              commit(SET_CONFIG, {
                key: "phonePrefixesList",
                value: response.phonePrefixesList
              });

              commit(SET_CONFIG, {
                key: "europeanCountries",
                value: response.europeanCountries
              });

              if (response.blueMediaGatewayList) {
                commit(SET_CONFIG, {
                  key: "bluemediaGatewayList",
                  value: Object.values(response.blueMediaGatewayList)
                });
              }

              if (response.blueMediaGatewayList) {
                commit(SET_CONFIG, {
                  key: "tinCountries",
                  value: Object.values(response.tinCountries)
                });
              }

            }

            state.isInitComplete = true;

            commit(SET_LOCK, false);

            // send event 0 (page loaded)
            state.analyticsService.sendStepEvent("");
          });
        } catch (e) {
          console.error(e);
          commit(SET_LOCK, false);
        }
      }
    },

    async validate({ commit, state }, model) {
      const response = await state.ajaxService.validate(model.name, model.value);
      if (ResponseStatus.CANCELLED !== response.status) {
        const isValid: ValidFieldInterface = {
          name: model.name,
          isValid: false
        };

        if (ResponseStatus.SUCCESS === response.status) {
          isValid.isValid = true;
          commit(SET_VALID_FIELD, isValid);
        } else if (response.errors) {
          const errors = Object.entries(response.errors).map(entry => {
            // map response entries to ResponseError
            return {
              field: entry[0],
              message: (entry[1] as any)[0]
            } as ResponseError;
          });

          commit(SET_VALID_FIELD, isValid);
          commit(ADD_ERRORS, errors);
        }
      }
    },

    async ajax({ commit, state }, payload: AjaxActionInterface) {
      await UtilityHelper.wait(() => state.isInitComplete);
      if (!state.isLocked) {
          commit(SET_LOCK, true);

        if (null !== payload.data) {
          if (state.tmsc) {
            payload.data['tmsc'] = state.tmsc;
          }
          if (state.query) {
            payload.data['source'] = state.query['from'];
          }
        }

        if (state.route === 'desktopDocumentUpload' || 
            state.route === 'desktopDocumentUploadBack' || 
            state.route === 'mobileDocumentUpload' || 
            state.route === 'mobileDocumentUploadBack') {
            state.isLoaderText = true
            state.isLoadingThumbnail = true
        }

        commit(SET_FORM_DATA, payload.data);

        if(state.formData) {
          state.analyticsService.setData({
            language_version: UtilityHelper.getLocale(),
            verification_method: state.formData.dataUploadChoice ? state.formData.dataUploadChoice : undefined,
            residency: (() => {
              let result = '';
              if (state.formData.residenceCountry) {
                result += state.formData.residenceCountry;
              }

              return result.length > 0 ? result : undefined;
            })(),
            currency: state.formData.currencyChoice ? state.formData.currencyChoice : undefined,
            us_taxpayer: state.formData.usaTaxPayer ? state.formData.usaTaxPayer : undefined,
            politically_exposed: state.formData.pepDeclaration ? state.formData.pepDeclaration : undefined,
            data_user: state.formData.dataUserDeclaration ? state.formData.dataUserDeclaration : undefined,
          });
        }


        // <editor-fold desc="Workaround for paymentGate being unreliable">
        if (!state.config.bluemediaGatewayList) {
          state.ajaxService.runAction(Action.DICTIONARIES, {}).then((response: any) => {
            if (response.blueMediaGatewayList) {
              commit(SET_CONFIG, {
                key: "bluemediaGatewayList",
                value: Object.values(response.blueMediaGatewayList)
              });
            }
          });
        }
        // </editor-fold>

        const container = document.querySelector("#app > .container");

        try {
          const response: ServerResponse = await state.ajaxService.runAction(
            payload.action,
            state.formData,
            payload.headers
          );

          let errors: ResponseError[] = [];

          if (response.errors) {
            errors = ErrorHelper.mapResponseErrors(response.errors);
          }

          if (response.dataContent) {
            state.consents = response.dataContent.consents ? response.dataContent.consents : []
          }

          commit(SET_ERRORS, errors);

          if (response.callback) {
            CallbackService.call(response.callback, response.args);
          }

          if (response.restart) {
            state.formData = {};
            commit(SET_STEP, "restart");
            await state.navigationService.goToRoute("restart");
          } else {
            if (response.next && state.currentStep !== response.next) {
              state.dataLayerService.pushEntry(state.currentStep);
              commit(SET_FORM_DATA, response.dataContent || {});
              commit(SET_STEP, response.next);
              //check additional store in case if we need to save some data from step before
              //in user-information.ts there is list of values which should be checked and added to temporary store
              for (const singleUserInfo in userInfo) {
                if (response.dataContent) {
                  if (singleUserInfo in response.dataContent) {
                    state.userInformation = { [singleUserInfo]: response.dataContent[`${singleUserInfo}`] };
                  }
                }
              }
              await state.navigationService.goToRoute(response.next as string);
            } else {
              if (
                !response.next &&
                (state.currentStep === "desktopDocumentUpload" || state.currentStep === "desktopDocumentUploadBack")
              ) {
                commit(SET_FORM_DATA, response.dataContent || {});
              }
            }
          }

          commit(SET_GLOBAL_ERROR, null);

          UtilityHelper.wait(() => {
            return container
              ? container !== document.querySelector("#app > .container") ||
              !response.next ||
              response.next !== state.currentStep
              : true;
          }).then(() => {
            commit(SET_LOCK, false);
            state.isLoaderText = false
            state.isLoadingThumbnail = false
        });
        commit(SET_RESPONSE, response);
      } catch (e) {
        console.error(e);
        commit(SET_GLOBAL_ERROR, e.message);
        if (e.restart) {
            commit(SET_STEP, "restart");
            state.ajaxService.restart();
            await state.navigationService.goToRoute("restart").catch((e: any) => console.error(e));
          }
          
          UtilityHelper.sleep(1000).then(() => {
            commit(SET_LOCK, false);
            state.isLoaderText = false
            state.isLoadingThumbnail = false
          });
        }
      } else {
        // todo: handle error
        commit(SET_LOCK, false);
        state.isLoaderText = false
        state.isLoadingThumbnail = false
      }
    }
  },
  getters: {
    isErrorsChanged(state: any): boolean {
      return state.isErrorsChanged;
    },
    errors(state: any): ResponseError[] {
      return state.errors;
    },
    isValidFieldsChanged(state: any): boolean {
      return state.isValidFieldsChanged;
    },
    validFields(state: any): Set<string> {
      return state.validFields;
    },
    globalError(state: any): string {
      return state.globalError;
    }
  }
});
